import { useCallback, useEffect, useRef, useState } from 'react';
import { ProductCard } from '../commerce/ProductCard.jsx';
import { SkeletonCard } from '../ui/Skeleton.jsx';
import { EmptyState } from '../ui/EmptyState.jsx';
import { cn } from '../../lib/format.js';
import { useReducedMotion } from '../../hooks/useReducedMotion.js';

/**
 * A home page shelf as one continuous line of products.
 *
 * Every shelf is a single row that scrolls sideways: swiped on a phone, and on
 * a desktop scrolled with the arrows or a trackpad. Two cards and the edge of
 * a third show on a phone, so it reads as a row that carries on; five show on
 * a desktop, the same width as the shop grid's cards.
 *
 * With `autoplay`, the row also glides on its own — slowly, in an endless
 * loop, in the given `direction` — so the page is moving before anyone
 * touches it. The products are drawn twice and the row wraps from the second
 * copy back to the first, which is what makes the loop seamless; the copy is
 * inert, so a keyboard or a screen reader meets each product once. It moves
 * the row's own scroll position rather than sliding a picture of it, so
 * swiping, the arrows, and a trackpad all still work, and:
 *
 *   - it pauses while a pointer rests on it or keyboard focus is inside
 *   - a swipe, a scroll, or an arrow holds it for a few seconds, then it
 *     picks up again from wherever the shopper left it
 *   - it stops off screen and in a background tab
 *   - under reduced motion it never starts; the row is still a plain,
 *     swipeable row with snapping
 */

const CARD = 'w-[44%] shrink-0 sm:w-[30%] lg:w-[calc((100%-4rem)/5)]';

/** Pixels per second. Slow enough to read a title as it passes. */
const SPEED = 30;
/** How long a touch, scroll, or arrow holds the row still. */
const RESUME_AFTER = 4000;

const Arrow = ({ direction, onClick, hidden, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    tabIndex={hidden ? -1 : 0}
    className={cn(
      'absolute top-[38%] z-10 hidden size-11 -translate-y-1/2 place-items-center rounded-full border border-mist bg-surface/95 text-ink shadow-[0_8px_24px_-12px_rgba(13,13,13,0.45)] transition-[opacity,background-color] duration-200 hover:bg-surface lg:grid',
      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
      direction < 0 ? '-left-5' : '-right-5',
      hidden && 'pointer-events-none opacity-0',
    )}
  >
    <svg viewBox="0 0 16 16" className={cn('size-4', direction < 0 && 'rotate-180')} fill="currentColor" aria-hidden="true">
      <path d="M6.2 3.2 5.14 4.26 8.88 8l-3.74 3.74L6.2 12.8 11 8z" />
    </svg>
  </button>
);

export const ProductRail = ({
  products,
  isPending,
  limit = 12,
  empty,
  onQuickAdd,
  priority = false,
  label,
  autoplay = false,
  direction = 1,
  className,
}) => {
  const scroller = useRef(null);
  const reduced = useReducedMotion();
  const [edges, setEdges] = useState({ start: true, end: true });

  const shown = isPending ? [] : (products ?? []).slice(0, limit);
  const looping = autoplay && !reduced && shown.length > 1;

  // Whether the row is being looked at or held, read by the animation loop.
  const hovered = useRef(false);
  const focused = useRef(false);
  const holdUntil = useRef(0);
  const hold = () => {
    holdUntil.current = performance.now() + RESUME_AFTER;
  };

  /** The width of one copy of the products: where the loop wraps. */
  const period = useCallback(() => {
    const element = scroller.current;
    if (!element || !looping) return 0;
    const first = element.children[0];
    const copy = element.children[shown.length];
    return first && copy ? copy.offsetLeft - first.offsetLeft : 0;
  }, [looping, shown.length]);

  const measure = useCallback(() => {
    const element = scroller.current;
    if (!element) return;

    // A shopper who scrolls past the end of the first copy is moved back by
    // exactly one copy, which looks the same — the row has no end to reach.
    const span = period();
    if (span > 0 && element.scrollLeft >= span) element.scrollLeft -= span;

    setEdges({
      start: !looping && element.scrollLeft <= 4,
      end: !looping && element.scrollLeft + element.clientWidth >= element.scrollWidth - 4,
    });
  }, [looping, period]);

  useEffect(() => {
    const element = scroller.current;
    if (!element) return undefined;
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [measure, products, isPending]);

  /* The glide. */
  useEffect(() => {
    const element = scroller.current;
    if (!looping || !element) return undefined;

    let visible = false;
    const seen = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
    }, { threshold: 0.15 });
    seen.observe(element);

    // A row running backwards starts one copy in, so it has room to move.
    let position = element.scrollLeft;
    if (direction < 0 && position < 1) {
      position = Math.max(0, period() - 1);
      element.scrollLeft = position;
    }

    let last = performance.now();
    let frame;

    const tick = (now) => {
      const elapsed = Math.min(64, now - last);
      last = now;
      const span = period();

      const moving =
        visible && span > element.clientWidth / 2 && !hovered.current && !focused.current &&
        now >= holdUntil.current && !document.hidden;

      if (moving) {
        // Picks up from wherever a swipe or an arrow left the row.
        if (Math.abs(element.scrollLeft - position) > 2) position = element.scrollLeft;
        position += (direction * SPEED * elapsed) / 1000;
        if (position >= span) position -= span;
        if (position <= 0) position += span;
        element.scrollLeft = position;
      } else {
        position = element.scrollLeft;
      }

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      seen.disconnect();
    };
  }, [looping, direction, period]);

  const page = (step) => {
    const element = scroller.current;
    if (!element) return;
    hold();
    element.scrollBy({ left: step * element.clientWidth * 0.9, behavior: reduced ? 'auto' : 'smooth' });
  };

  if (!isPending && !products?.length) {
    return empty ? <EmptyState {...empty} /> : null;
  }

  const items = looping ? [...shown, ...shown] : shown;

  return (
    <div
      className={cn('relative', className)}
      onPointerEnter={(event) => event.pointerType === 'mouse' && (hovered.current = true)}
      onPointerLeave={(event) => event.pointerType === 'mouse' && (hovered.current = false)}
      onFocus={() => (focused.current = true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) focused.current = false;
      }}
    >
      <ul
        ref={scroller}
        onScroll={measure}
        onPointerDown={hold}
        onWheel={hold}
        onTouchStart={hold}
        aria-label={label}
        className={cn(
          '-mx-4 flex gap-4 overflow-x-auto scroll-px-4 px-4 pb-2 sm:-mx-6 sm:scroll-px-6 sm:px-6 lg:mx-0 lg:scroll-px-0 lg:px-0',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          // Snapping would fight a row that is moving itself.
          !looping && 'snap-x snap-mandatory',
        )}
      >
        {isPending
          ? Array.from({ length: 5 }, (_, index) => (
              <li key={index} className={CARD}>
                <SkeletonCard />
              </li>
            ))
          : items.map((product, index) => {
              const copy = index >= shown.length;
              return (
                <li
                  key={copy ? `${product._id}-again` : product._id}
                  className={cn(CARD, 'flex', !looping && 'snap-start')}
                  aria-hidden={copy || undefined}
                  inert={copy || undefined}
                >
                  <ProductCard
                    product={product}
                    priority={priority && index < 5}
                    onQuickAdd={onQuickAdd?.(product)}
                    className="w-full"
                  />
                </li>
              );
            })}
      </ul>

      <Arrow direction={-1} label="Scroll back" hidden={edges.start} onClick={() => page(-1)} />
      <Arrow direction={1} label="Scroll for more" hidden={edges.end} onClick={() => page(1)} />
    </div>
  );
};

export default ProductRail;
